import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Sparkles, Palette, Layers, ChevronDown, BadgeCheck } from "lucide-react";
import { FadeIn, Reveal } from "../components/Motion";
import TimberTechExplorer from "../components/TimberTechExplorer";
import Seo from "../components/Seo";
import { TT_COMPOSITE, TT_PVC, TT_RAILING } from "../data/timbertech";
import { track } from "../lib/track";

const DEFAULTS = {
  overline: "Premium Composite & PVC Decking",
  headline: "TimberTech decking, built the Zarco's way.",
  intro:
    "TimberTech is our primary premium composite and PVC decking family — the low-maintenance alternative to wood we recommend first for North Georgia decks. We'll walk you through the exact collections, colors, and pricing during your free on-site estimate.",
  credentials: "",
  composite_title: "TimberTech Composite Decking",
  composite_body:
    "A wood-fiber blend core wrapped in a protective polymer cap — the look of real wood grain without annual staining or sealing. A strong fit for most North Georgia decks.",
  pvc_title: "TimberTech Advanced PVC",
  pvc_body:
    "Fully synthetic decking with no wood content — a premium choice for maximum moisture resistance, ideal for shaded, lakeside, poolside, or low-clearance builds. We'll help you compare both lines for your yard's conditions.",
  good_better_best:
    "Every Zarco's estimate includes a clear Good / Better / Best comparison across TimberTech's collections — so you can weigh appearance, texture, and budget side by side before deciding. Specific collection names and current pricing are reviewed with you on-site.",
  warranty_note:
    "TimberTech decking is backed by manufacturer warranties that vary by collection. We review the exact warranty coverage for the collection you choose during your estimate — in writing.",
  faqs: [],
};

const CONSIDERATIONS = [
  { icon: Droplets, title: "Moisture & weather", body: "North Georgia humidity, storms, and freeze-thaw cycles are hard on wood. Capped composite and Advanced PVC are built to shrug them off — we'll match the line to your yard's exposure." },
  { icon: Sparkles, title: "Maintenance", body: "No annual staining, sealing, or sanding. Occasional soap-and-water cleaning keeps a TimberTech deck looking new for years." },
  { icon: Palette, title: "Colors & style", body: "A wide range of wood-look colors and finishes across TimberTech's collections. We bring real samples to your estimate so you can see them against your home." },
  { icon: Layers, title: "Railing compatibility", body: "Complete the space with compatible railing systems — composite or aluminum — chosen to fit your deck, your home, and your budget." },
];

export default function TimberTech() {
  const [c, setC] = useState(DEFAULTS);
  const [openFaq, setOpenFaq] = useState(null);
  const [explorer, setExplorer] = useState(null); // "composite" | "pvc" | "railing" | null
  const [catalog, setCatalog] = useState({ composite: TT_COMPOSITE, pvc: TT_PVC, railing: TT_RAILING });

  useEffect(() => {
    track("timbertech_page_view");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/timbertech`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setC({ ...DEFAULTS, ...d }))
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/timbertech_catalog`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.composite) {
          setCatalog({
            composite: d.composite || TT_COMPOSITE,
            pvc: d.pvc || TT_PVC,
            railing: d.railing || TT_RAILING,
          });
        }
      })
      .catch(() => {});
  }, []);

  const faqs = Array.isArray(c.faqs) ? c.faqs : [];

  return (
    <div data-testid="timbertech-page" className="bg-white">
      <Seo
        title="TimberTech Composite & PVC Decking | Zarco's Decks — North Georgia"
        description="Explore TimberTech composite and Advanced PVC decking colors, collections, and railing options with Zarco's Decks. Physical samples at your free on-site estimate."
      />
      {/* HERO — premium light architectural treatment */}
      <section className="relative bg-[#FBF8F3] text-[#111] pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-20 overflow-hidden border-b border-[#E7E2D9]">
        {/* subtle stone-depth wash, top-right */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#E8D6C2]/40 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-[#E7E2D9]" />
        <div className="relative grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <FadeIn className="lg:col-span-7">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              {c.overline}
            </p>
            <h1
              data-testid="tt-headline"
              className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl tracking-tighter font-medium leading-[0.98] text-[#111]"
            >
              {c.headline}
            </h1>
            <p data-testid="tt-intro" className="mt-7 text-base md:text-lg text-[#4A4A4A] max-w-2xl leading-relaxed">
              {c.intro}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="tt-cta-btn"
                onClick={() => track("timbertech_estimate_click", { location: "hero" })}
                className="group bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 font-semibold tracking-wide inline-flex items-center gap-3"
              >
                Get Your Free Estimate{" "}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/gallery"
                data-testid="tt-gallery-btn"
                className="border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors px-8 py-4 font-semibold tracking-wide"
              >
                See Real Projects
              </Link>
            </div>

            {/* CREDENTIALS — renders only when an official designation is earned & added via CMS */}
            {c.credentials ? (
              <div
                data-testid="tt-credentials"
                className="mt-10 inline-flex items-center gap-3 bg-white border border-[#C27A3E]/60 px-5 py-3"
              >
                <BadgeCheck size={20} className="text-[#99582A]" />
                <span className="text-sm font-semibold tracking-wide text-[#111]">{c.credentials}</span>
              </div>
            ) : null}

            {/* Official TimberTech brand mark + future credential badge / profile link (CMS-gated) */}
            <div data-testid="tt-brand-area" className="mt-12 flex flex-wrap items-center gap-6">
              <div className="bg-white border border-[#E7E2D9] px-6 py-4 shadow-[0_16px_40px_-24px_rgba(17,17,17,0.18)]">
                <img
                  src="/brand/timbertech-logo.svg"
                  alt="TimberTech — official brand logo"
                  data-testid="tt-brand-logo"
                  className="w-[150px] md:w-[170px] h-auto"
                />
              </div>
              {c.credential_badge ? (
                <img
                  src={c.credential_badge}
                  alt="Official TimberTech contractor credential"
                  data-testid="tt-credential-badge"
                  className="h-16 md:h-20 w-auto shrink-0 object-contain"
                />
              ) : null}
              {c.profile_url ? (
                <a
                  href={c.profile_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tt-profile-link"
                  className="text-sm font-semibold text-[#99582A] hover:text-[#111] transition-colors inline-flex items-center gap-2 underline underline-offset-4 decoration-[#99582A]/40"
                >
                  View Our TimberTech Profile
                </a>
              ) : null}
            </div>
          </FadeIn>
          <Reveal delay={0.15} className="lg:col-span-5">
            {/* framed project card — premium depth without dark fills */}
            <div className="relative">
              <div aria-hidden="true" className="absolute -top-4 -right-4 w-full h-full border border-[#C27A3E]/40" />
              <div className="relative bg-white border border-[#E7E2D9] p-3 shadow-[0_32px_70px_-34px_rgba(17,17,17,0.28)]">
                <div className="img-hover img-spotlight aspect-[4/3] w-full">
                  <img
                    src="/service-trex-composite.webp"
                    alt="Premium composite deck built by Zarco's Decks in North Georgia"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="px-2 pt-3 pb-1 text-xs text-[#666] italic">A recent Zarco's Decks composite build</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPOSITE vs ADVANCED PVC */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">Two Ways to Build</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111] max-w-3xl">
            One premium family. Two technologies.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { n: "01", title: c.composite_title, body: c.composite_body, testid: "tt-composite-card", mode: "composite", cta: "Explore colors" },
            { n: "02", title: c.pvc_title, body: c.pvc_body, testid: "tt-pvc-card", mode: "pvc", cta: "Explore colors" },
            { n: "03", title: "TimberTech Railing", body: "Complete your outdoor space with railing systems designed to complement TimberTech decking.", testid: "tt-railing-card", mode: "railing", cta: "Explore railing" },
          ].map((card, i) => (
            <Reveal key={card.n} delay={i * 0.1} className="h-full">
              <button
                onClick={() => setExplorer(card.mode)}
                data-testid={card.testid}
                aria-label={`${card.title} — ${card.cta}`}
                className="zd-card-lift group w-full h-full text-left bg-white border border-[#E7E2D9] hover:border-[#99582A] p-8 md:p-10 flex flex-col cursor-pointer transition-colors"
              >
                <span className="font-display text-3xl tracking-tight font-medium text-[#99582A]">{card.n}</span>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-5 text-[#111] group-hover:text-[#99582A] transition-colors">
                  {card.title}
                </h3>
                <p className="mt-4 text-[#4A4A4A] leading-relaxed flex-1">{card.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#99582A]">
                  {card.cta}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POST-EXPLORER CONVERSION — collection guidance → free estimate */}
      <section className="pb-24 md:pb-32 px-6 md:px-12 lg:px-20 bg-white">
        <Reveal
          data-testid="tt-guidance-cta"
          className="relative overflow-hidden border border-[#E7E2D9] bg-[#FBF8F3] px-8 py-12 md:p-16 text-center"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-[#E8D6C2]/40 blur-3xl" />
          <p className="relative text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">Free On-Site Guidance</p>
          <h2 className="relative font-display text-3xl md:text-5xl tracking-tight font-medium mt-4 text-[#111] max-w-3xl mx-auto">
            Not sure which TimberTech collection is right for your deck?
          </h2>
          <p className="relative mt-5 text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto">
            During your free on-site estimate, we'll help you compare colors, collections,
            and railing options — and walk through the overall scope of your project with
            real samples in hand.
          </p>
          <Link
            to="/contact"
            data-testid="tt-guidance-cta-btn"
            onClick={() => track("timbertech_estimate_click", { location: "guidance" })}
            className="relative group mt-9 inline-flex items-center gap-3 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-9 py-4 font-semibold tracking-wide"
          >
            Schedule Your Free On-Site Estimate
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>

      {/* OTHER BRANDS CALLOUT — TimberTech stays primary, Trex never presented as equal */}
      <section className="pb-24 md:pb-32 px-6 md:px-12 lg:px-20 bg-white">
        <Reveal data-testid="tt-other-brands-callout" className="border border-[#E7E2D9] bg-[#FBF8F3] p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium text-[#111]">
              Prefer another decking brand?
            </h3>
            <p className="mt-4 text-[#4A4A4A] leading-relaxed">
              TimberTech is our primary decking recommendation, but every project is different.
              We're happy to discuss other quality decking options based on your home's design,
              your preferences, and your budget.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="tt-other-brands-cta"
            className="group shrink-0 border-2 border-[#111] hover:bg-[#111] hover:text-white transition-colors px-7 py-4 font-semibold tracking-wide inline-flex items-center gap-3"
          >
            Discuss Your Project <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>

      {/* CONSIDERATIONS */}
      <section data-testid="tt-considerations" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#FBF8F3]">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">What Homeowners Ask About</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
            Built around how you'll actually live on it.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CONSIDERATIONS.map(({ icon: Icon, title, body }, i) => (
            <Reveal
              key={title}
              delay={i * 0.08}
              data-testid={`tt-consideration-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
              className="zd-card-lift group bg-white border border-[#E7E2D9] p-8"
            >
              <div className="w-12 h-12 bg-[#FBF8F3] border border-[#E7E2D9] flex items-center justify-center transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105">
                <Icon size={20} strokeWidth={1.5} className="text-[#99582A]" />
              </div>
              <h3 className="font-display text-xl tracking-tight font-medium mt-6 text-[#111]">{title}</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">{body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GOOD / BETTER / BEST + WARRANTY */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal data-testid="tt-gbb" className="lg:col-span-7 bg-[#FBF8F3] border border-[#E7E2D9] p-10 md:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">Good · Better · Best</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-4 text-[#111]">
              Options for every budget — compared in writing.
            </h2>
            <p className="mt-5 text-[#4A4A4A] leading-relaxed">{c.good_better_best}</p>
          </Reveal>
          <Reveal delay={0.12} data-testid="tt-warranty" className="lg:col-span-5 border border-[#E7E2D9] p-10 md:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">Warranty</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium mt-4 text-[#111]">
              Backed by the manufacturer.
            </h3>
            <p className="mt-4 text-[#4A4A4A] leading-relaxed">{c.warranty_note}</p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#FBF8F3]">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">Homeowner FAQs</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
              Straight answers before you commit.
            </h2>
          </Reveal>
          <div className="mt-12 max-w-4xl divide-y divide-[#E7E2D9] border-t border-b border-[#E7E2D9]">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.05} y={16}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`tt-faq-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span className="font-display text-lg md:text-xl tracking-tight font-medium text-[#111] group-hover:text-[#99582A] transition-colors">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-[#99582A] transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height] duration-500 ease-out ${openFaq === i ? "max-h-60" : "max-h-0"}`}
                >
                  <p className="pb-6 text-[#4A4A4A] leading-relaxed max-w-3xl">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="bg-[#2A2A2A] text-white px-6 md:px-12 lg:px-20 py-20 md:py-24">
        <Reveal className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E8D6C2] font-semibold">
              Free, no-obligation estimate
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4">
              Compare TimberTech options for your yard.
            </h2>
            <p className="mt-4 text-white/85 max-w-xl">
              On-site walk-through, real samples, and a written Good / Better / Best
              comparison — with Trex and other quality options available if they're
              a better fit for your project.
            </p>
          </div>
          <div className="flex flex-col items-start gap-4">
            <Link
              to="/contact"
              data-testid="tt-bottom-cta-btn"
              onClick={() => track("timbertech_estimate_click", { location: "bottom_cta" })}
              className="bg-[#C27A3E] text-white hover:bg-white hover:text-[#111] transition-colors px-10 py-5 font-semibold tracking-wide flex items-center gap-3"
            >
              Get Your Free Estimate <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact?financing=1"
              data-testid="tt-financing-link"
              onClick={() => track("financing_interest_click", { location: "timbertech_bottom_cta" })}
              className="text-sm text-white/70 hover:text-white underline underline-offset-4 decoration-white/40 hover:decoration-white transition-colors"
            >
              Interested in financing your TimberTech project? Let us know when requesting your estimate.
            </Link>
          </div>
        </Reveal>
      </section>

      {/* PRODUCT EXPLORER — in-page modal, keeps homeowner on Zarco's */}
      <TimberTechExplorer
        open={explorer !== null}
        mode={explorer || "composite"}
        onClose={() => setExplorer(null)}
        composite={catalog.composite}
        pvc={catalog.pvc}
        railing={catalog.railing}
      />
    </div>
  );
}
